import React, { useState, useEffect, useMemo, useRef } from 'react';
import './User.css';
import Dropdown from './Dropdown';
import type { Workshop } from '../utils/workshops';
import DatePicker from './Date';
import { useInHouse } from '../context/inHousePermissions';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import { EXPRESS_PRICE_KZT } from '../utils/expressHelpers';
import { formatRelative } from '../utils/formatRelative';
import { canViewField, canEditField, canMakeRefund, ARCHIVE_ROLES } from '../utils/roleAccess';
import RefundModal from './RefundModal';
import CdekWaybillModal from './CdekWaybillModal';

// Поля, которые реально живут в orders (плюс username из users). Любое поле
// вне этого набора в PATCH /orders не уйдёт — бэк его всё равно проигнорирует
// или упадёт, а лишний шум в логах изменений не нужен.
type OrderStatus =
  | 'Пишет'
  | 'Заполнил'
  | 'Отредачено'
  | 'Готово под печать'
  | 'Отправлено на печать'
  | 'Отпечатано'
  | 'Отправлено'
  | 'Доставлено'
  | 'Архивировано'
  | 'Возврат';

type ExpressType = 'none' | 'express_print' | 'express_edit' | 'urep' | null;

export interface OrderUser {
  id?: number;
  userId: number | string;
  username: string;
  status: OrderStatus;
  purchaseDate: Date;
  country: string | null;
  city: string;
  address: string;
  recipient: string;
  currency: string;
  platform: string;
  comment: string;
  commentLogis: string;
  express: ExpressType;
  /** Цех печати. null — источник попапа не прислал поле (считаем неизвестным). */
  workshop: Workshop | null;
  manager: string;
  editor: string;
  logist: string;
  logMethod: string;
  courierNumber: string;
  cdekNumber: string;
  whatsapp: string;
  telegram: string;
  isInfluencer: boolean;
  finishedAt: Date | null;
  /** Индивидуальная полная стоимость заказа. null = считается по эталону
   *  FULL_PRICE по валюте (миграция 0032). */
  fullAmount?: number | null;
}

interface OrderLogRow {
  id: number;
  staffId: number | null;
  staffName: string | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

// Подписи полей в истории. Чего тут нет — покажем как есть, лучше сырое
// имя поля, чем молча спрятанная строка.
const LOG_FIELD_LABEL: Record<string, string> = {
  status: 'Статус',
  fullAmount: 'Общая сумма',
  express: 'Экспресс',
  workshop: 'Цех',
  logMethod: 'Способ доставки',
  logist: 'Логист',
  editor: 'Редактор',
  manager: 'Менеджер',
  comment: 'Комментарий',
  commentLogis: 'Комментарий логистики',
  cdekNumber: 'Накладная СДЭК',
  courierNumber: 'Номер курьера',
  address: 'Адрес',
  city: 'Город',
  country: 'Страна',
  recipient: 'Получатель',
  contact_access: 'Доступ к анкете',
};

interface UserProps {
  // принимаем любую структуру (роутеры сейчас передают свою Order-форму),
  // приводим к OrderUser внутри.
  user: any;
  onClose: () => void;
  onUpdate: (updatedUser: any) => void;
  deadline: string;
}

interface Notification {
  message: string;
  timestamp: string;
  userId: string;
  author?: string | null;
}

interface Transaction {
  id: number;
  amount: number;
  currency: string;
  bank: string;
  paymentType: string;
  manager: string;
  receipt?: string;
  createdAt: string;
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  prepayment: 'Предоплата',
  postpayment: 'Постоплата',
  full_payment: 'Полная',
  express_print: 'Эксп. печать',
  express_edit: 'Уск. ред.',
  urep: 'УРЭП',
  delivery: 'Доставка',
  additional: 'Доп.',
};

// Валюты, поддерживаемые бэкендом (см. currencyEnum в schema.ts).
const SALE_CURRENCIES = ['KZT', 'USD', 'AZN', 'BYN', 'GEL', 'TRY', 'RUB', 'EUR', 'KGS'] as const;

const BANK_LABEL: Record<string, string> = {
  vtb: 'ВТБ',
  tinkoff: 'Тинькофф',
  kaspi: 'Kaspi',
  kaspi_gold: 'Kaspi Gold',
  kaspi_pay: 'Kaspi Pay',
  optima: 'Optima',
  stripe: 'Stripe',
  halyk: 'Halyk',
  cash: 'Наличные',
};

// Поля, которые реально патчим в orders. Пресекает попытку отправить мусор.
const PATCHABLE_FIELDS: (keyof OrderUser)[] = [
  'status',
  'country',
  'city',
  'address',
  'recipient',
  'platform',
  'comment',
  'commentLogis',
  'express',
  'manager',
  'editor',
  'logist',
  'logMethod',
  'courierNumber',
  'cdekNumber',
  'whatsapp',
  'telegram',
];

// Способы доставки. Свободный текст в БД, на фронте — фиксированный список.
const LOG_METHOD_OPTIONS = ['sdek', 'indriver', 'dhl', 'курьер', 'kazpost', 'авиа'];

// «Возврат» в списке нет намеренно: возврат оформляется кнопкой «Сделать
// возврат» (нужны причина и сумма), а статус бэк проставляет сам, и только
// при полном возврате. У уже возвращённых заказов статус показывается
// read-only — см. Field «Статус».
const STATUS_OPTIONS: OrderStatus[] = [
  'Пишет',
  'Заполнил',
  'Отредачено',
  'Готово под печать',
  'Отправлено на печать',
  'Отпечатано',
  'Отправлено',
  'Доставлено',
  'Архивировано',
];

const PLATFORM_OPTIONS = ['Таргет', 'Сарафанка', 'Тикток', 'Были подписаны', 'Не помнит'];

// Express dropdown: внутреннее значение (enum) ↔ отображаемая ru-метка.
const EXPRESS_OPTIONS: { value: Exclude<ExpressType, null>; label: string }[] = [
  { value: 'none', label: 'Нет' },
  { value: 'express_print', label: 'Экспресс печать' },
  { value: 'express_edit', label: 'Уск. редактура' },
  { value: 'urep', label: 'УРЭП' },
];

const FIELD_LABELS: Record<string, string> = {
  status: 'Статус',
  country: 'Страна',
  city: 'Город',
  address: 'Адрес',
  recipient: 'Получатель',
  platform: 'Откуда покупка',
  comment: 'Комментарий редакторам',
  commentLogis: 'Комментарий логистике',
  express: 'Экспресс',
  workshop: 'Цех',
  manager: 'Менеджер',
  editor: 'Редактор',
  logist: 'Логист',
  logMethod: 'Способ доставки',
  courierNumber: 'Курьер',
  cdekNumber: 'Номер СДЭК',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  isInfluencer: 'Инфлюенсер',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  videoUrl: 'Ссылка на видео',
  videoUploadDate: 'Дата выкладки',
  deadlineDate: 'Дедлайн',
};

interface InfluencerData {
  tiktok: string;
  instagram: string;
  videoUrl: string;
  videoUploadDate: Date | null;
  deadlineDate: Date | null;
}

const INFLUENCER_FIELDS: (keyof InfluencerData)[] = [
  'tiktok',
  'instagram',
  'videoUrl',
  'videoUploadDate',
  'deadlineDate',
];

function emptyInfluencer(): InfluencerData {
  return {
    tiktok: '',
    instagram: '',
    videoUrl: '',
    videoUploadDate: null,
    deadlineDate: null,
  };
}

function normalizeInfluencer(raw: any): InfluencerData {
  return {
    tiktok: raw?.tiktok ?? '',
    instagram: raw?.instagram ?? '',
    videoUrl: raw?.videoUrl ?? '',
    videoUploadDate: raw?.videoUploadDate ? new Date(raw.videoUploadDate) : null,
    deadlineDate: raw?.deadlineDate ? new Date(raw.deadlineDate) : null,
  };
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  'Пишет': 'st-writing',
  'Заполнил': 'st-filled',
  'Отредачено': 'st-edited',
  'Готово под печать': 'st-approved',
  'Отправлено на печать': 'st-sent-print',
  'Отпечатано': 'st-printed',
  'Отправлено': 'st-shipped',
  'Доставлено': 'st-delivered',
  'Архивировано': 'st-archived',
  'Возврат': 'st-returned',
};

const formatDateShort = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

const formatDateTime = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const User: React.FC<UserProps> = ({ user, onClose, onUpdate, deadline }) => {
  const [editableUser, setEditableUser] = useState<OrderUser>(() => normalizeUser(user));
  const [originalUser, setOriginalUser] = useState<OrderUser>(() => normalizeUser(user));
  // Накладная создана из админки (есть uuid) — её можно изменить или удалить.
  const [hasWaybill, setHasWaybill] = useState<boolean>(() => !!user?.cdekUuid);
  const [waybillOpen, setWaybillOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [refundOpen, setRefundOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  // Общая сумма заказа: правится прямо в шапке транзакций, отдельно от
  // общей формы — менять её ходят целенаправленно, а не заодно с адресом.
  const [fullAmountDraft, setFullAmountDraft] = useState<string>('');
  const [fullAmountSaving, setFullAmountSaving] = useState(false);
  const [fullAmountSaved, setFullAmountSaved] = useState(false);
  // Эталонные цены по валютам. У заказа full_amount обычно NULL — это значит
  // «считать по прайсу», и пустое поле в шапке ничего не говорило человеку.
  // Поэтому подставляем фактическую сумму, по которой заказ и считается.
  const [fullPrices, setFullPrices] = useState<Record<string, number>>({});
  // С чем сравнивать при сохранении: если человек ничего не менял, в базу
  // не пишем — иначе подставленный прайс молча стал бы явной суммой.
  const fullAmountInitial = useRef<string>('');
  // История заказа из order_logs: смены статусов и полей, кто и когда.
  const [history, setHistory] = useState<OrderLogRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userPassword, setUserPassword] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Попап «Поменять экспресс» — создаёт транзакцию УР/ЭП/УРЭП. Бэк сам
  // синхронизирует orders.express + book_archives.express.
  const [expressModalOpen, setExpressModalOpen] = useState(false);
  const [exMType, setExMType] = useState<'express_print' | 'express_edit' | 'urep'>('express_print');
  const [exMAmount, setExMAmount] = useState<string>(String(EXPRESS_PRICE_KZT.express_print));
  const [exMCurrency, setExMCurrency] = useState('KZT');
  const [exMBank, setExMBank] = useState('kaspi');
  const [exMSaving, setExMSaving] = useState(false);
  const [exMError, setExMError] = useState<string | null>(null);

  // Доп-секция «Инфлюенсер» — поля живут в таблице influencers, отдельно
  // от orders. Подтягиваем только когда isInfluencer=true, чтобы не дёргать
  // лишний раз обычных юзеров.
  const [influencerData, setInfluencerData] = useState<InfluencerData>(emptyInfluencer());
  const [originalInfluencerData, setOriginalInfluencerData] =
    useState<InfluencerData>(emptyInfluencer());
  const { currentUser } = useAuth();
  const { inHouseRecords, currentUserRecord } = useInHouse();

  const canView = (field: string) =>
    !!currentUser && canViewField(currentUserRecord?.role, field);
  // Архивирование и возврат — доступ шире, чем правка статуса: редактор
  // должен убирать из работы брошенные книги, но не двигать логистику.
  const canArchive =
    !!currentUser && ARCHIVE_ROLES.includes(currentUserRecord?.role || '');

  const canEdit = (field: string) =>
    !!currentUser &&
    canEditField(currentUserRecord?.role, field, currentUserRecord?.permissions.canEdit);

  // Догружаем актуальные данные ордера по username (роутеры передают подмножество).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/orders/${user.username}`);
        if (cancelled) return;
        const merged = normalizeUser({ ...user, ...data });
        setEditableUser(merged);
        setOriginalUser(merged);
      } catch (err) {
        console.error('Error fetching user data', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.username]);

  // Догружаем юзер-данные (для показа пароля менеджеру в шапке).
  // Legacy-юзера могут иметь bcrypt-хеш — показываем "—".
  useEffect(() => {
    if (!user.username) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/admin/user/${user.username}`);
        if (cancelled) return;
        const pw = String(data?.password ?? '');
        setUserPassword(pw.startsWith('$2') ? '—' : pw);
      } catch (err) {
        console.error('Error fetching user password', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.username]);

  // Уведомления — грузим сразу при открытии попапа (секция не прячется).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const purchaseISO =
          editableUser.purchaseDate instanceof Date
            ? editableUser.purchaseDate.toISOString()
            : new Date(editableUser.purchaseDate).toISOString();

        // Сразу все уведомления по этому юзеру (с момента покупки).
        const { data } = await client.get('/notifications', {
          params: { since: purchaseISO, orderBy: 'timestamp', order: 'desc' },
        });
        if (cancelled) return;
        setNotifications(
          (data as Notification[]).filter((n) => n.message?.includes(editableUser.username))
        );
      } catch (err) {
        console.error('Error fetching notifications', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editableUser.purchaseDate, editableUser.username]);

  // Инфлюенсер-метаданные. Бэк отдаёт нулевой объект если строки ещё нет
  // (для legacy isInfluencer-ордеров до миграции на таблицу influencers).
  useEffect(() => {
    if (!editableUser.isInfluencer) return;
    const id = Number(editableUser.userId);
    if (!id || isNaN(id)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/influencers/${id}`);
        if (cancelled) return;
        const normalized = normalizeInfluencer(data);
        setInfluencerData(normalized);
        setOriginalInfluencerData(normalized);
      } catch (err) {
        console.error('Error fetching influencer data', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editableUser.isInfluencer, editableUser.userId]);

  useEffect(() => {
    client
      .get<Record<string, number>>('/admin/full-prices')
      .then((r) => setFullPrices(r.data || {}))
      .catch((err) => console.error('Error fetching full prices', err));
  }, []);

  // История заказа — рядом с транзакциями, тем же триггером открытия попапа.
  useEffect(() => {
    const orderId = Number(editableUser.id);
    if (!orderId || isNaN(orderId)) return;
    let cancelled = false;
    setHistoryLoading(true);
    (async () => {
      try {
        const { data } = await client.get<OrderLogRow[]>(`/orders/${orderId}/history`);
        if (!cancelled) setHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching order history', err);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editableUser.id]);

  // Черновик суммы держим в строке, чтобы поле можно было очистить —
  // пустое значит «по прайсу», то есть null в базе. Но показываем не пустоту,
  // а эталон по валюте заказа: в поле должна стоять та сумма, по которой
  // заказ реально считается.
  useEffect(() => {
    const next =
      editableUser.fullAmount != null
        ? String(editableUser.fullAmount)
        : fullPrices[editableUser.currency] != null
          ? String(fullPrices[editableUser.currency])
          : '';
    setFullAmountDraft(next);
    fullAmountInitial.current = next;
  }, [editableUser.fullAmount, editableUser.currency, fullPrices]);

  // Лента событий заказа. Правки полей из order_logs — только часть картины:
  // у свежего заказа их нет вовсе, и блок выглядел пустым, хотя события были.
  // Поэтому подмешиваем создание заказа, платежи и завершение книги. Завершение
  // логом не пишется — это отметка времени на самом заказе.
  const timeline = useMemo(() => {
    const rows: { key: string; at: Date; what: string; detail: string; who: string }[] = [];

    if (editableUser.purchaseDate) {
      rows.push({
        key: 'created',
        at: new Date(editableUser.purchaseDate),
        what: 'Заказ создан',
        detail: editableUser.status ? `статус ${editableUser.status}` : '—',
        who: editableUser.manager || '—',
      });
    }

    for (const t of transactions) {
      rows.push({
        key: `tx-${t.id}`,
        at: new Date(t.createdAt),
        what: 'Платёж',
        detail: `${t.amount.toLocaleString('ru-RU')} ${t.currency} · ${
          PAYMENT_TYPE_LABEL[t.paymentType] || t.paymentType
        }`,
        who: t.manager || '—',
      });
    }

    // Завершение книги и лог «статус → Заполнил» — одно и то же событие:
    // статус ставит бэкенд в тот же момент, когда клиент закончил анкету.
    // Показываем одной строкой: заголовок понятнее у события, переход —
    // у лога, автор «клиент» (в логе его нет, там пусто — это не сотрудник).
    const finishedAt = editableUser.finishedAt ? new Date(editableUser.finishedAt) : null;
    const filledLog = finishedAt
      ? history.find(
          (h) =>
            h.field === 'status' &&
            h.newValue === 'Заполнил' &&
            Math.abs(new Date(h.createdAt).getTime() - finishedAt.getTime()) < 5 * 60 * 1000,
        )
      : undefined;

    if (finishedAt) {
      rows.push({
        key: 'finished',
        at: finishedAt,
        what: 'Книга заполнена',
        detail: filledLog
          ? `${filledLog.oldValue || '—'} → ${filledLog.newValue || '—'}`
          : '—',
        who: 'клиент',
      });
    }

    for (const h of history) {
      if (filledLog && h.id === filledLog.id) continue;
      rows.push({
        key: `log-${h.id}`,
        at: new Date(h.createdAt),
        what: LOG_FIELD_LABEL[h.field] || h.field,
        detail: `${h.oldValue || '—'} → ${h.newValue || '—'}`,
        who: h.staffName || '—',
      });
    }

    return rows.sort((a, b) => b.at.getTime() - a.at.getTime());
  }, [
    editableUser.purchaseDate,
    editableUser.status,
    editableUser.manager,
    editableUser.finishedAt,
    transactions,
    history,
  ]);

  const saveFullAmount = async () => {
    const raw = fullAmountDraft.trim();
    const parsed = raw === '' ? null : Math.round(Number(raw));
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      alert('Сумма должна быть положительным числом или пустой');
      return;
    }
    // Ничего не меняли — выходим. Сравниваем с тем, что было показано, а не
    // с базой: показанный прайс не должен превращаться в явную сумму сам.
    if (fullAmountDraft.trim() === fullAmountInitial.current.trim()) return;
    if ((editableUser.fullAmount ?? null) === parsed) return;
    setFullAmountSaving(true);
    try {
      await client.patch(`/orders/${editableUser.username}`, { fullAmount: parsed });
      setEditableUser((u) => ({ ...u, fullAmount: parsed }));
      onUpdate({ ...editableUser, fullAmount: parsed });
      fullAmountInitial.current = raw;
      setFullAmountSaved(true);
      setTimeout(() => setFullAmountSaved(false), 1500);
    } catch (err) {
      console.error('Failed to update full amount', err);
      alert('Не удалось изменить общую сумму');
      setFullAmountDraft(
        editableUser.fullAmount != null ? String(editableUser.fullAmount) : '',
      );
    } finally {
      setFullAmountSaving(false);
    }
  };

  // Транзакции — подгружаем при открытии попапа, фильтр по userId.
  useEffect(() => {
    const id = Number(editableUser.userId);
    if (!id || isNaN(id)) return;
    let cancelled = false;
    setTransactionsLoading(true);
    (async () => {
      try {
        const { data } = await client.get('/sales', {
          params: { userId: id, limit: 50 },
        });
        if (!cancelled) setTransactions(data as Transaction[]);
      } catch (err) {
        console.error('Error fetching transactions', err);
      } finally {
        if (!cancelled) setTransactionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editableUser.userId]);

  const setField = <K extends keyof OrderUser>(field: K, value: OrderUser[K]) => {
    setEditableUser((prev) => ({ ...prev, [field]: value }));
  };

  const setInfluencerField = <K extends keyof InfluencerData>(
    field: K,
    value: InfluencerData[K],
  ) => {
    setInfluencerData((prev) => ({ ...prev, [field]: value }));
  };

  // Список изменённых полей (имена) — для счётчика и отправки только дельты.
  const changedFields = useMemo(() => {
    const out: string[] = [];
    for (const f of PATCHABLE_FIELDS) {
      const a = serializeForCompare(editableUser[f]);
      const b = serializeForCompare(originalUser[f]);
      if (a !== b) out.push(f as string);
    }
    return out;
  }, [editableUser, originalUser]);

  const changedInfluencerFields = useMemo(() => {
    if (!editableUser.isInfluencer) return [] as (keyof InfluencerData)[];
    const out: (keyof InfluencerData)[] = [];
    for (const f of INFLUENCER_FIELDS) {
      const a = serializeForCompare(influencerData[f]);
      const b = serializeForCompare(originalInfluencerData[f]);
      if (a !== b) out.push(f);
    }
    return out;
  }, [editableUser.isInfluencer, influencerData, originalInfluencerData]);

  // Отправка СДЭКом без номера накладной. Бэкенд это же условие проверяет
  // сам (PATCH /orders вернёт 400) — здесь дублируем, чтобы логист увидел
  // проблему в поле, а не в тосте после неудачного сохранения.
  const cdekMissing =
    editableUser.status === 'Отправлено' &&
    originalUser.status !== 'Отправлено' &&
    editableUser.logMethod === 'sdek' &&
    !editableUser.cdekNumber.trim();

  const handleSave = async () => {
    if (
      !currentUser ||
      (changedFields.length === 0 && changedInfluencerFields.length === 0)
    )
      return;
    if (cdekMissing) return; // кнопка и так заблокирована, это страховка
    setLoading(true);
    try {
      if (changedFields.length > 0) {
        const patch: Record<string, any> = {};
        for (const f of changedFields) {
          const v = (editableUser as any)[f];
          patch[f] = v instanceof Date ? v.toISOString() : v;
        }
        await client.patch(`/orders/${editableUser.username}`, patch);
      }

      // Влиятельной-секция (только при isInfluencer=true) — PATCH в отдельную
      // таблицу через /influencers/:userId. Бэк upsert'ит, если строки ещё нет.
      if (changedInfluencerFields.length > 0) {
        const patch: Record<string, any> = {};
        for (const f of changedInfluencerFields) {
          const v = (influencerData as any)[f];
          patch[f] = v instanceof Date ? v.toISOString() : v;
        }
        const id = Number(editableUser.userId);
        if (Number.isFinite(id)) {
          await client.patch(`/influencers/${id}`, patch);
        }
      }

      // Уведомления больше не шлём отсюда: смену статуса фиксирует бэкенд
      // (PATCH /orders создаёт уведомление при изменении status), а событие
      // «завершил книгу» — бэкенд при заполнении. Так не плодятся дубли и шум
      // по каждому полю.

      // Поведение архивации — оставляем как было, отдельный POST в /sales/archive.
      const becameArchived =
        originalUser.status !== 'Архивировано' && editableUser.status === 'Архивировано';
      if (becameArchived) {
        try {
          const dateString = editableUser.purchaseDate.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          await client.post('/sales/archive', {
            date: dateString,
            userId: editableUser.userId,
          });
        } catch (err) {
          console.error('Failed to update sales document:', err);
        }
      }

      setOriginalUser(editableUser);
      setOriginalInfluencerData(influencerData);
      onUpdate(editableUser);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1500);
      onClose();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setLoading(false);
    }
  };

  const statusBadgeCls = STATUS_BADGE_CLASS[editableUser.status] || '';

  return (
    <div className="u-bg" onClick={onClose}>
      <div className="u-modal" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="u-head">
          <div className="u-head-main">
            <div className="u-head-top">
              <h2
                className="u-username"
                style={{ color: editableUser.isInfluencer ? '#FFC000' : undefined }}
              >
                {editableUser.username}
              </h2>
              {userPassword && (
                <span className="u-head-pw" title="Пароль юзера">
                  {userPassword}
                </span>
              )}
              <span className={`u-status-badge ${statusBadgeCls}`}>
                {editableUser.status}
              </span>
              {canView('isInfluencer') && editableUser.isInfluencer && (
                <span className="u-influencer-badge">Инфлюенсер</span>
              )}
              {canView('express') && editableUser.express && editableUser.express !== 'none' && (
                <span className="u-express-badge">
                  {editableUser.express === 'express_print'
                    ? 'Эксп. печать'
                    : editableUser.express === 'express_edit'
                      ? 'Уск. ред.'
                      : 'УРЭП'}
                </span>
              )}
            </div>
            <div className="u-head-meta">
              <span>Куплен {formatRelative(editableUser.purchaseDate)}</span>
              <span className="u-dot">·</span>
              <span>{editableUser.currency}</span>
              {editableUser.finishedAt && (
                <>
                  <span className="u-dot">·</span>
                  <span>Завершён {formatDateShort(editableUser.finishedAt)}</span>
                </>
              )}
            </div>
          </div>
          <button className="u-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>

        {/* ── Body: 2 columns ───────────────────────────────────── */}
        <div className="u-body">
          {/* Левая колонка */}
          <div className="u-col">
            <Group title="Заказ">
              <Field label="Статус" show={canView('status')}>
                {canEdit('status') && editableUser.status !== 'Возврат' ? (
                  <Dropdown
                    value={editableUser.status}
                    options={STATUS_OPTIONS}
                    onChange={(v) => setField('status', v as OrderStatus)}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ReadValue value={editableUser.status} />
                    {/* Редактору статус целиком не отдаём — только архив.
                        Дропдаун открыл бы ему и «Отправлено», и «Доставлено»,
                        а нужно ровно два перехода: убрать книгу из работы и
                        вернуть. Возврат — всегда на «Заполнил». */}
                    {canArchive && editableUser.status !== 'Возврат' && (
                      <button
                        type="button"
                        className="u-link-btn"
                        onClick={() =>
                          setField(
                            'status',
                            (editableUser.status === 'Архивировано'
                              ? 'Заполнил'
                              : 'Архивировано') as OrderStatus,
                          )
                        }
                      >
                        {editableUser.status === 'Архивировано'
                          ? 'Вернуть в работу'
                          : 'Архивировать'}
                      </button>
                    )}
                  </div>
                )}
              </Field>

              <Field label="Экспресс" show={canView('express')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ReadValue
                    value={EXPRESS_OPTIONS.find((o) => o.value === editableUser.express)?.label}
                  />
                  {canEdit('express') && (
                    <button
                      type="button"
                      className="u-link-btn"
                      onClick={() => {
                        setExMType('express_print');
                        setExMAmount(String(EXPRESS_PRICE_KZT.express_print));
                        setExMCurrency('KZT');
                        setExMBank('kaspi');
                        setExMError(null);
                        setExpressModalOpen(true);
                      }}
                    >
                      Поменять
                    </button>
                  )}
                </div>
              </Field>

              <Field label="Откуда покупка" show={canView('platform')}>
                {canEdit('platform') ? (
                  <Dropdown
                    value={editableUser.platform}
                    options={PLATFORM_OPTIONS}
                    onChange={(v) => setField('platform', v)}
                  />
                ) : (
                  <ReadValue value={editableUser.platform} />
                )}
              </Field>

              {canMakeRefund(currentUserRecord?.role) && (
                <div className="u-refund-cell">
                  <button
                    type="button"
                    className="u-refund-btn"
                    onClick={() => setRefundOpen(true)}
                  >
                    Сделать возврат
                  </button>
                </div>
              )}

            </Group>

            <Group title="Команда">
              <Field label="Менеджер" show={canView('manager') || canView('salesMan')}>
                {canEdit('manager') || canEdit('salesMan') ? (
                  <Dropdown
                    value={editableUser.manager}
                    options={(() => {
                      // В дропдауне менеджеров — sales, operations и admin.
                      // Если текущее значение поля не входит ни в один live-
                      // staff (например, сотрудника удалили или роль сменили
                      // после назначения) — всё равно показываем его в списке,
                      // чтобы не потерять и не «обнулить» назначение случайно.
                      const live = Object.values(inHouseRecords)
                        .filter((r: any) => ['sales', 'operations', 'admin'].includes(r.role))
                        .map((r: any) => r.name);
                      const cur = editableUser.manager;
                      return cur && !live.includes(cur) ? [...live, cur] : live;
                    })()}
                    onChange={(v) => setField('manager', v)}
                  />
                ) : (
                  <ReadValue value={editableUser.manager} />
                )}
              </Field>

              <Field label="Редактор" show={canView('editor')}>
                {canEdit('editor') ? (
                  <Dropdown
                    value={editableUser.editor || ''}
                    options={Object.values(inHouseRecords)
                      .filter((r: any) => r.role === 'editor' || r.role === 'headeditor')
                      .map((r: any) => r.name)}
                    onChange={(v) => setField('editor', v)}
                  />
                ) : (
                  <ReadValue value={editableUser.editor} />
                )}
              </Field>

              <Field label="Логист" show={canView('logist')}>
                {canEdit('logist') ? (
                  <Dropdown
                    value={editableUser.logist || ''}
                    options={Object.values(inHouseRecords)
                      .filter((r: any) => r.role === 'log')
                      .map((r: any) => r.name)}
                    onChange={(v) => setField('logist', v)}
                  />
                ) : (
                  <ReadValue value={editableUser.logist} />
                )}
              </Field>
            </Group>
          </div>

          {/* Правая колонка */}
          <div className="u-col">
            <Group title="Доставка">
              <Field label="Страна" show={canView('country')}>
                {canEdit('country') ? (
                  <input
                    className="u-input"
                    type="text"
                    value={editableUser.country ?? ''}
                    onChange={(e) => setField('country', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.country} />
                )}
              </Field>

              <Field label="Город" show={canView('city')}>
                {canEdit('city') ? (
                  <input
                    className="u-input"
                    type="text"
                    value={editableUser.city}
                    onChange={(e) => setField('city', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.city} />
                )}
              </Field>

              <Field label="Адрес" show={canView('address')}>
                {canEdit('address') ? (
                  <textarea
                    className="u-textarea u-textarea-mini"
                    value={editableUser.address}
                    onChange={(e) => setField('address', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.address} />
                )}
              </Field>

              <Field label="Получатель" show={canView('recipient')}>
                {canEdit('recipient') ? (
                  <input
                    className="u-input"
                    type="text"
                    value={editableUser.recipient}
                    onChange={(e) => setField('recipient', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.recipient} />
                )}
              </Field>

              <Field label="Способ доставки" show={canView('logMethod')}>
                {canEdit('logMethod') ? (
                  <Dropdown
                    value={editableUser.logMethod || ''}
                    options={LOG_METHOD_OPTIONS}
                    onChange={(v) => setField('logMethod', v)}
                  />
                ) : (
                  <ReadValue value={editableUser.logMethod} />
                )}
              </Field>

              <Field label="Номер СДЭК" show={canView('cdekNumber')}>
                {canEdit('cdekNumber') ? (
                  <input
                    className={`u-input${cdekMissing ? ' u-input--error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    placeholder={cdekMissing ? 'обязательно для СДЭК' : ''}
                    value={editableUser.cdekNumber}
                    onChange={(e) => setField('cdekNumber', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.cdekNumber} />
                )}
                {hasWaybill && editableUser.userId && (
                  <button type="button" className="u-waybill-btn" onClick={() => setWaybillOpen(true)}>
                    Изменить накладную
                  </button>
                )}
              </Field>

              <Field label="Курьер" show={canView('courierNumber')}>
                {canEdit('courierNumber') ? (
                  <input
                    className="u-input"
                    type="text"
                    value={editableUser.courierNumber}
                    onChange={(e) => setField('courierNumber', e.target.value)}
                  />
                ) : (
                  <ReadValue value={editableUser.courierNumber} />
                )}
              </Field>
            </Group>

            <Group title="Связь">
              <Field label="WhatsApp" show={canView('whatsapp')}>
                {canEdit('whatsapp') ? (
                  <input
                    className="u-input"
                    type="text"
                    inputMode="numeric"
                    value={editableUser.whatsapp}
                    onChange={(e) =>
                      setField('whatsapp', e.target.value.replace(/\D/g, '').slice(0, 15))
                    }
                  />
                ) : (
                  <ReadValue value={editableUser.whatsapp} />
                )}
              </Field>
              <Field label="Telegram" show={canView('telegram')}>
                {canEdit('telegram') ? (
                  <input
                    className="u-input"
                    type="text"
                    value={editableUser.telegram}
                    onChange={(e) => setField('telegram', e.target.value)}
                    placeholder="@username или +77001234567"
                  />
                ) : (
                  <ReadValue value={editableUser.telegram} />
                )}
              </Field>
            </Group>

            {editableUser.isInfluencer && (
              <Group title="Инфлюенсер">
                <Field label="TikTok">
                  <input
                    className="u-input"
                    type="text"
                    value={influencerData.tiktok}
                    onChange={(e) => setInfluencerField('tiktok', e.target.value)}
                    placeholder="никнейм без @"
                  />
                </Field>
                <Field label="Instagram">
                  <input
                    className="u-input"
                    type="text"
                    value={influencerData.instagram}
                    onChange={(e) => setInfluencerField('instagram', e.target.value)}
                    placeholder="никнейм без @"
                  />
                </Field>
                <Field label="Дедлайн">
                  <DatePicker
                    value={influencerData.deadlineDate}
                    onChange={(d) => setInfluencerField('deadlineDate', d)}
                  />
                </Field>
              </Group>
            )}
          </div>
        </div>

        {/* ── Comments group (full-width, compact) ──────────────── */}
        {(canView('comment') || canView('commentLogis')) && (
          <div className="u-comments">
            <div className="u-group-title u-group-title-inline">Комментарии</div>
            <div className="u-comments-grid">
              {canView('comment') && (
                <div className="u-field">
                  <label className="u-label">Редакторам</label>
                  <textarea
                    className="u-textarea u-textarea-compact"
                    value={editableUser.comment}
                    onChange={(e) => setField('comment', e.target.value)}
                    placeholder="—"
                  />
                </div>
              )}
              {canView('commentLogis') && (
                <div className="u-field">
                  <label className="u-label">Логистике</label>
                  <textarea
                    className="u-textarea u-textarea-compact"
                    value={editableUser.commentLogis}
                    onChange={(e) => setField('commentLogis', e.target.value)}
                    placeholder="—"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Footer (sticky) ───────────────────────────────────── */}
        <footer className="u-foot">
          <div className="u-foot-info">
            {(() => {
              const allChanged = [
                ...changedFields,
                ...changedInfluencerFields.map((f) => f as string),
              ];
              if (allChanged.length === 0) {
                return <span className="u-saved-hint">Все изменения сохранены</span>;
              }
              return (
                <span className="u-changes">
                  {allChanged.length} изменени{allChanged.length === 1 ? 'е' : 'я'}:{' '}
                  {allChanged.map((f) => FIELD_LABELS[f] || f).join(', ')}
                </span>
              );
            })()}
          </div>
          <div className="u-foot-actions">
            {cdekMissing && (
              <span className="u-foot-warn">Укажите номер СДЭК</span>
            )}
            <button className="u-btn-ghost" onClick={onClose} disabled={loading}>
              Отмена
            </button>
            <button
              className="u-btn-primary"
              onClick={handleSave}
              disabled={
                loading ||
                cdekMissing ||
                (changedFields.length === 0 && changedInfluencerFields.length === 0)
              }
            >
              {loading ? 'Сохранение…' : success ? '✓ Сохранено' : 'Сохранить'}
            </button>
          </div>
        </footer>

        {/* ── Transactions ──────────────────────────────────────── */}
        <section className="u-tx">
          <div className="u-tx-head">
            <span>Транзакции</span>
            {transactions.length > 0 && (
              <span className="u-notif-count">· {transactions.length}</span>
            )}
            {/* Общая сумма заказа. Правят её admin и operations: от неё
                считаются сбор постоплаты, гейт печати и доплата клиентом,
                поэтому менеджеру её двигать нельзя. Пусто = по прайсу. */}
            {canEdit('fullAmount') && (
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 400,
                }}
              >
                <span style={{ color: 'var(--text-3)' }}>Общая сумма</span>
                <input
                  type="number"
                  value={fullAmountDraft}
                  disabled={fullAmountSaving}
                  onChange={(e) => setFullAmountDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveFullAmount();
                  }}
                  placeholder="по прайсу"
                  style={{
                    width: 110,
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'var(--card)',
                    color: 'inherit',
                    fontSize: 13,
                  }}
                />
                <span style={{ color: 'var(--text-3)' }}>
                  {editableUser.currency || ''}
                </span>
                {/* Сохранение отдельной кнопкой: по уходу с поля было
                    непонятно, записалось или нет. Кнопка появляется только
                    когда значение действительно изменили. */}
                {fullAmountSaved && (
                  <span style={{ color: 'var(--ok, #2e7d32)' }}>сохранено</span>
                )}
                {fullAmountDraft.trim() !== fullAmountInitial.current.trim() && (
                  <button
                    type="button"
                    className="u-link-btn"
                    disabled={fullAmountSaving}
                    onClick={saveFullAmount}
                  >
                    {fullAmountSaving ? 'Сохранение…' : 'Сохранить'}
                  </button>
                )}
              </span>
            )}
          </div>
          {transactionsLoading ? (
            <div className="u-notif-empty">Загрузка…</div>
          ) : transactions.length === 0 ? (
            <div className="u-notif-empty">Платежей нет</div>
          ) : (
            <table className="u-tx-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Сумма</th>
                  <th>Тип</th>
                  <th>Банк</th>
                  <th>Менеджер</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td>{formatRelative(t.createdAt)}</td>
                    <td className="u-tx-amount">
                      {t.amount.toLocaleString('ru-RU')}{' '}
                      {currentUserRecord?.role === 'admin' ? (
                        <select
                          className="u-tx-currency"
                          value={t.currency}
                          onChange={async (e) => {
                            const next = e.target.value;
                            const prev = t.currency;
                            // optimistic update
                            setTransactions((arr) =>
                              arr.map((x) => (x.id === t.id ? { ...x, currency: next } : x)),
                            );
                            try {
                              await client.patch(`/sales/${t.id}`, { currency: next });
                            } catch (err) {
                              console.error('Failed to update currency', err);
                              setTransactions((arr) =>
                                arr.map((x) => (x.id === t.id ? { ...x, currency: prev } : x)),
                              );
                              alert('Не удалось изменить валюту');
                            }
                          }}
                        >
                          {SALE_CURRENCIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ) : (
                        t.currency
                      )}
                    </td>
                    <td>{PAYMENT_TYPE_LABEL[t.paymentType] || t.paymentType}</td>
                    <td>{BANK_LABEL[t.bank] || t.bank}</td>
                    <td>{t.manager || '—'}</td>
                  </tr>
                ))}
                <tr className="u-tx-total">
                  <td>Итого</td>
                  <td className="u-tx-amount">
                    {transactions
                      .reduce((sum, t) => sum + (t.amount || 0), 0)
                      .toLocaleString('ru-RU')}{' '}
                    {transactions[0]?.currency || ''}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tbody>
            </table>
          )}
        </section>

        {/* ── История заказа (order_logs) ───────────────────────── */}
        <section className="u-tx">
          <div className="u-tx-head">
            <span>История</span>
            {timeline.length > 0 && (
              <span className="u-notif-count">· {timeline.length}</span>
            )}
          </div>
          {historyLoading ? (
            <div className="u-notif-empty">Загрузка…</div>
          ) : timeline.length === 0 ? (
            <div className="u-notif-empty">Событий нет</div>
          ) : (
            <table className="u-tx-table">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Что</th>
                  <th>Было → стало</th>
                  <th>Кто</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((row) => (
                  <tr key={row.key}>
                    <td>{formatRelative(row.at)}</td>
                    <td>{row.what}</td>
                    <td>{row.detail}</td>
                    <td>{row.who}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* ── Notifications (всегда видны) ───────────────────────── */}
        <section className="u-notif">
          <div className="u-notif-toggle">
            <span>Уведомления</span>
          </div>
          <div className="u-notif-body">
            {notifications.length === 0 ? (
              <div className="u-notif-empty">Пока ничего</div>
            ) : (
              notifications.map((n, i) => (
                <div key={i} className="u-notif-item">
                  <div className="u-notif-row">
                    <span className="u-notif-author">
                      {n.author || inHouseRecords[n.userId]?.name || 'Система'}
                    </span>
                    <span className="u-notif-time">{formatRelative(n.timestamp)}</span>
                  </div>
                  <div className="u-notif-msg">{n.message}</div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {waybillOpen && (
        <CdekWaybillModal
          userId={Number(editableUser.userId)}
          mode="edit"
          onClose={() => setWaybillOpen(false)}
          onDeleted={(status) => {
            // Бэк стёр номер и вернул «Отправлено» в «Отпечатано».
            setHasWaybill(false);
            const patch = { cdekNumber: '', status: status as OrderStatus };
            const next = { ...editableUser, ...patch };
            setEditableUser(next);
            setOriginalUser((prev) => ({ ...prev, ...patch }));
            onUpdate(next);
          }}
        />
      )}

      {refundOpen && (
        <RefundModal
          userId={Number(editableUser.userId)}
          username={editableUser.username}
          onClose={() => setRefundOpen(false)}
          onDone={({ statusChanged }) => {
            setRefundOpen(false);
            // Полный возврат бэк уже перевёл в «Возврат» — синхронизируем и
            // editable, и original, чтобы это не считалось несохранённым
            // изменением, и отдаём наверх: списки показывают статус.
            if (statusChanged) {
              const next = { ...editableUser, status: 'Возврат' as OrderStatus };
              setEditableUser(next);
              setOriginalUser((prev) => ({ ...prev, status: 'Возврат' as OrderStatus }));
              onUpdate(next);
            }
          }}
        />
      )}

      {expressModalOpen && (
        <div
          className="u-sub-bg"
          onClick={() => !exMSaving && setExpressModalOpen(false)}
        >
          <div className="u-sub-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="u-sub-title">Поменять экспресс</h3>
            {exMError && <div className="u-sub-error">{exMError}</div>}

            <label className="u-sub-field">
              <span>Тип</span>
              <select
                className="u-select"
                value={exMType}
                onChange={(e) => {
                  const v = e.target.value as 'express_print' | 'express_edit' | 'urep';
                  setExMType(v);
                  setExMAmount(String(EXPRESS_PRICE_KZT[v]));
                }}
              >
                <option value="express_print">Экспресс печать · 9000 ₸</option>
                <option value="express_edit">Ускоренная редактура · 6000 ₸</option>
                <option value="urep">УРЭП · 15000 ₸</option>
              </select>
            </label>

            <label className="u-sub-field">
              <span>Сумма</span>
              <input
                className="u-input"
                type="number"
                value={exMAmount}
                onChange={(e) => setExMAmount(e.target.value)}
              />
            </label>

            <div className="u-sub-row">
              <label className="u-sub-field" style={{ flex: 1 }}>
                <span>Валюта</span>
                <select
                  className="u-select"
                  value={exMCurrency}
                  onChange={(e) => setExMCurrency(e.target.value)}
                >
                  {['KZT', 'USD', 'AZN', 'BYN', 'GEL', 'TRY', 'RUB', 'EUR', 'KGS'].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="u-sub-field" style={{ flex: 1 }}>
                <span>Счёт</span>
                <select
                  className="u-select"
                  value={exMBank}
                  onChange={(e) => setExMBank(e.target.value)}
                >
                  {['kaspi','kaspi_gold','kaspi_pay','vtb','tinkoff','optima','halyk','stripe','cash','other'].map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="u-sub-actions">
              <button
                type="button"
                className="u-sub-btn"
                disabled={exMSaving}
                onClick={() => setExpressModalOpen(false)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="u-sub-btn u-sub-btn--primary"
                disabled={exMSaving}
                onClick={async () => {
                  setExMSaving(true);
                  setExMError(null);
                  try {
                    await client.post('/transactions', {
                      userId: Number(editableUser.userId),
                      paymentType: exMType,
                      amount: Number(exMAmount) || 0,
                      currency: exMCurrency,
                      bank: exMBank,
                    });
                    // Локально обновим и editable, и original — чтобы это
                    // не считалось «изменённым» при следующем сейве (orders.express
                    // бэк уже сам проставил при создании транзакции).
                    setEditableUser((prev) => ({ ...prev, express: exMType }));
                    setOriginalUser((prev) => ({ ...prev, express: exMType }));
                    setExpressModalOpen(false);
                  } catch (err: any) {
                    setExMError(
                      err?.response?.data?.error || err?.message || 'Ошибка'
                    );
                  } finally {
                    setExMSaving(false);
                  }
                }}
              >
                {exMSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── helpers / sub-components ───────────────────────────

// Превращаем разные формы (legacy User, серверный orders row) в OrderUser.
function normalizeUser(raw: any): OrderUser {
  return {
    id: raw?.id,
    userId: raw?.userId ?? raw?.id ?? raw?.username,
    username: raw?.username ?? '',
    status: (raw?.status ?? 'Пишет') as OrderStatus,
    purchaseDate: raw?.purchaseDate ? new Date(raw.purchaseDate) : new Date(),
    country: raw?.country ?? '',
    city: raw?.city ?? '',
    address: raw?.address ?? '',
    recipient: raw?.recipient ?? '',
    currency: raw?.currency ?? 'KZT',
    // Без этой строки общая сумма не доходила из заказа в попап: поле
    // показывало прайс, а сохранённое значение сбрасывалось на следующем
    // обновлении пропсов.
    fullAmount: raw?.fullAmount ?? null,
    platform: raw?.platform ?? '',
    comment: raw?.comment ?? '',
    commentLogis: raw?.commentLogis ?? '',
    express: (raw?.express ?? null) as ExpressType,
    workshop: (raw?.workshop ?? null) as Workshop | null,
    // Поддерживаем legacy-имя salesMan, чтобы не сломать места, которые ещё его шлют.
    manager: raw?.manager ?? raw?.salesMan ?? '',
    editor: raw?.editor ?? '',
    logist: raw?.logist ?? '',
    logMethod: raw?.logMethod ?? '',
    courierNumber: raw?.courierNumber ?? '',
    cdekNumber: raw?.cdekNumber ?? '',
    whatsapp: raw?.whatsapp ?? '',
    telegram: raw?.telegram ?? '',
    isInfluencer: !!raw?.isInfluencer,
    finishedAt: raw?.finishedAt ? new Date(raw.finishedAt) : null,
  };
}

function serializeForCompare(v: any): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString();
  return String(v);
}

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="u-group">
    <div className="u-group-title">{title}</div>
    <div className="u-group-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; show?: boolean; children: React.ReactNode }> = ({
  label,
  show = true,
  children,
}) => {
  if (!show) return null;
  return (
    <div className="u-field">
      <label className="u-label">{label}</label>
      {children}
    </div>
  );
};

const ReadValue: React.FC<{ value: any }> = ({ value }) => (
  <div className="u-read">{value === null || value === undefined || value === '' ? '—' : String(value)}</div>
);

export default User;
